/* eslint-disable max-len */
import debounce from 'lodash/debounce';

/**
* NetConstruct State Machine. Allows tracking of multiple states for the browser.
*/
const self = {};

const addEvent = window.attachEvent || window.addEventListener;
let initialised = false;
let interval = null;

/** Global module options. */
self.options = {
  interval: 1000,
};

/** Array of states in the state machine. */
self.states = [];

/** Array of active states in the state machine. */
self.currentStates = [];

/** Array of configurations to be used to evaluate the state machine. */
self.configurations = [];

/**
 * Initialise the state machine.
 */
self.init = function init(options) {
  if (initialised === false) {
    // Set global options
    self.options = mergeOptions(self.options, options);

    // Add default tests to state machine
    addDefaultTests();

    // Perform initial evaluation
    evaluateStateMachine();

    // Clear existing interval if required
    if (interval !== null) {
      clearInterval(interval);
    }

    // Add interval to evaluate state machine
    interval = setInterval(evaluateStateMachine, self.options.interval);

    // Add window resize event (w. IE8 fallback)
    addEvent('resize', debounce(evaluateStateMachine));
  }

  initialised = true;

  return self;
};

/**
 * Add a state to the state machine.
 * @param {string} The name of the state.
 * @param {object} The options for the state.
 */
self.addState = function addState(name, options) {
      // Check state does not exist
  if (findByName(self.states, name) !== null) {
    return self;
  }

      // Add new state to existing state array
  self.states.push(new State(name, options));

      // Sort states
  self.states = sortByOptionsKey(self.states, 'minWidth');

  return self;
};

/**
 * Get a state from the state machine.
 * @param {string} The name of the state.
 */
self.getState = function getState(name) {
  if (typeof name === 'string') {
    for (let i = self.states.length - 1; i >= 0; i -= 1) {
      if (self.states[i].name === name) {
        return self.states[i];
      }
    }
  }
  return self.states;
};

/**
 * Remove a state from the state machine.
 * @param {string} The name of the state.
 */
self.removeState = function removeState(name) {
  for (let i = self.states.length - 1; i >= 0; i -= 1) {
    if (self.states[i].name === name) {
      self.states.splice(i, 1);
    }
  }

  return self;
};

/**
 * Remove all states from the state machine.
 */
self.removeAllStates = function removeAllStates() {
  self.states = self.currentStates = [];

  return self;
};

/**
 * Update a state in the state machine.
 * @param {string} The name of the state.
 * @param {object} The options for the state.
 */
self.updateState = function updateState(name, options) {
  for (let i = self.states.length - 1; i >= 0; i -= 1) {
    if (self.states[i].id === name) {
      self.states[i].options = mergeOptions(self.states[i].options, options);
    }
  }

  return this;
};

/**
 * Check if the specified state is active.
 * @param {string} The name of the state.
 */
self.isActive = function isActive(name) {
  for (let i = 0; i < self.currentStates.length; i += 1) {
    if (self.currentStates[i].name === name) {
      return true;
    }
  }

  return false;
};

/**
 * Add a configuration to the state machine.
 * @param {string} The name of the configuration.
 * @param {function} The test for the configuration.
 */
self.addConfiguration = function addConfiguration(name, test) {
  if (name !== '' && test !== null) {
    self.configurations.push(new Config(name, test));
  }

  return self;
};

/**
 * Get a configuration from the state machine.
 * @param {string} The name of the configuration.
 */
self.getConfiguration = function getConfiguration(name) {
  if (typeof name === 'string') {
    for (let i = self.configurations.length - 1; i >= 0; i -= 1) {
      if (self.configurations[i].name === name) {
        return self.configurations[i];
      }
    }
  }
  return self.configurations;
};

/**
 * Remove a configuration from the state machine.
 * @param {string} The name of the configuration.
 */
self.removeConfiguration = function removeConfiguration(name) {
  for (let i = self.configurations.length - 1; i >= 0; i -= 1) {
    if (self.configurations[i].name === name) {
      self.configurations.splice(i, 1);
    }
  }
};

/**
 * Add Bootstrap 3.0.0 responsive states.
 */
self.bootstrap = function bootstrap() {
  const sm = 768;
  const md = 992;
  const lg = 1200;

  self
    .addState('xs', {
      maxWidth: (sm - 1),
    })
    .addState('sm', {
      minWidth: sm,
      maxWidth: (md - 1),
    })
    .addState('md', {
      minWidth: md,
      maxWidth: (lg - 1),
    })
    .addState('lg', {
      minWidth: lg,
    });

  return self;
};

/**
 * Add touch states.
 */
self.touch = function touch() {
  self
    .addState('touch', {
      touch: true,
    })
    .addState('no-touch', {
      touch: false,
    });

  return self;
};

/**
 * Add the default tests to the state machine.
 */
function addDefaultTests() {
  // Add minimum width configuration
  self.addConfiguration('minWidth', (state) => {
    if (typeof state.options.minWidth === 'number' && state.options.minWidth <= window.innerWidth) {
      return true;
    }

    return false;
  });

  // Add maximum width configuration
  self.addConfiguration('maxWidth', (state) => {
    if (typeof state.options.maxWidth === 'number' && state.options.maxWidth >= window.innerWidth) {
      return true;
    }

    return false;
  });

  // Add minimum height configuration
  self.addConfiguration('minHeight', (state) => {
    if (typeof state.options.minHeight === 'number' && state.options.minWidth <= window.innerHeight) {
      return true;
    }

    return false;
  });

  // Add maximum height configuration
  self.addConfiguration('maxHeight', (state) => {
    if (typeof state.options.maxHeight === 'number' && state.options.maxWidth >= window.innerHeight) {
      return true;
    }

    return false;
  });

  // Add touch configuration - requires Modernizr
  self.addConfiguration('touch', (state) => {
    if (typeof state.options.touch === 'boolean' && Modernizr && (Modernizr.touch === state.options.touch)) {
      return true;
    }

    return false;
  });
}

/**
 * Evaluate all states in the state machine.
 */
function evaluateStateMachine() {
  const activeStates = [];
  let previousStates = [];
  let enteredEvents = [];
  let exitedEvents = [];

  for (let i = 0; i < self.states.length; i += 1) {
    const state = self.states[i];
    let isActive = true;

    for (let j = 0; j < self.configurations.length; j += 1) {
      const config = self.configurations[j];

      if (config.evaluate(state) === false) {
        isActive = false;
        break;
      }
    }

          // Check if state was active
    const wasActive = (findByName(self.currentStates, state.name) !== null);

          // Add active state to current states
    if (isActive) {
              // Add new active states to entered array
      if (wasActive === false) {
        enteredEvents = enteredEvents.concat(state.onEnter);
      }

      activeStates.push(state);
    } else if (wasActive) {
      // Add previously active states to exited array
      exitedEvents = exitedEvents.concat(state.onExit);
    }
  }

  // Store previous states.
  previousStates = self.currentStates;

  // Store current states.
  self.currentStates = activeStates;

  // Create event data for callbacks.
  const eventData = {
    currentStates: activeStates,
    previousStates,
  };

  // Handle exited state events
  for (let l = 0; l < exitedEvents.length; l += 1) {
    exitedEvents[l](eventData);
  }

      // Handle entered state events
  for (let k = 0; k < enteredEvents.length; k += 1) {
    enteredEvents[k](eventData);
  }
}

/**
 * Sort an array by a specified key.
 * @param {array} The array to be sorted.
 * @param {string} The name of the attribute to sort by.
 */
function findByName(array, name) {
  for (let i = 0; i < array.length; i += 1) {
    if (array[i].name === name) {
      return array[i];
    }
  }

  return null;
}

/**
 * Sort an array by a specified key.
 * @param {array} The array to be sorted.
 * @param {string} The name of the attribute to sort by.
 */
function sortByOptionsKey(array, key) {
  return array.sort((a, b) => {
    const x = a.options[key];
    const y = b.options[key];
    return ((x < y) ? -1 : ((x > y) ? 1 : 0)); // eslint-disable-line
  });
}

/**
 * Merge two objects.
 * @param {object} The first object to merge.
 * @param {object} The second object to merge.
 */
function mergeOptions(obj1, obj2) {
  const obj3 = {};

  if (obj1) {
    Object.keys(obj1).forEach((key) => {
      obj3[key] = obj1[key];
    });
  }

  if (obj2) {
    Object.keys(obj2).forEach((key) => {
      obj3[key] = obj2[key];
    });
  }

  return obj3;
}

/**
 * Creates a state.
 * @class Represents a single state in the state machine.
 * @param {string} The name of the state.
 * @param {object} The options for the state.
 */
function State(name, options) {
  const state = this;

  /** Default options for state. */
  const defaults = {
    minWidth: 0,
    maxWidth: 99999,
  };

  // Merge options with defaults
  options = mergeOptions(defaults, options);

  /** Name of the state. */
  state.name = name;

  /** Options for the state. */
  state.options = options;

  /** Functions to fire when state is entered. */
  state.onEnter = options.onEnter ? [options.onEnter] : [];

  /** Functions to fire when state is exited. */
  state.onExit = options.onExit ? [options.onExit] : [];

  /**
   * Bind to event.
   * @param {string} Name of the event to bind to.
   */
  state.on = function on(event, callback) {
    if (typeof event !== 'string') {
      return null;
    }

    if (typeof callback !== 'function') {
      return null;
    }

    switch (event) {
      case 'enter':
        state.onEnter.push(callback);
        break;

      case 'exit':
        state.onExit.push(callback);
        break;

      default:
        break;
    }

    return state;
  };

  /**
   * Get the parent state machine for chaining.
   */
  state.machine = function machine() {
    return self;
  };

  return state;
}

/**
 * Creates a configuration.
 * @class Represents a single configuration option in the state machine.
 * @param {string} The name of the configuration.
 * @param {function} The test for the configuration. Should return true if the state is active.
 */
function Config(name, test) {
  const config = this;

  /** Name of the configuration. */
  config.name = name;

  /** Function to execute as the test. */
  config.test = test;

  /**
   * Evaluate the configuration against the specified state.
   * @param {State} The state to evaluate against.
   */
  config.evaluate = function evaluate(state) {
    if (typeof state.options[config.name] === 'undefined') {
      return null;
    }

    return config.test(state);
  };

  /**
   * Get the parent state machine for chaining.
   */
  config.machine = function machine() {
    return self;
  };

  return config;
}

export default self;
